import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { isValidPostal } from '../components/voucher/voucher-validation';
import companiesService, { defaultCompanyData } from '../services/companies-service';
import { useReviewRowEditReporter } from '../components/voucher/ReviewTab/ReviewEditTrackingContext';

const eventTargetType = {
  text: true,
  number: true,
};

const MIN_COMPANY_SEARCH_LENGTH = 1;

export default function useCompanyState(
  saved = {},
  type,
  onUpdate,
  syncKey = 0,
  enabled = true,
  options = {},
) {
  const { duplicateOnNameBlur = true } = options;
  const { id } = saved;
  const defaultCountry = 'United States';
  const mounted = useRef(true);
  const selectionMadeRef = useRef(false);
  const savedCompanyNameRef = useRef(saved?.name || '');
  const skipNextHydrationRef = useRef(null);
  const reportEdit = useReviewRowEditReporter({ rowType: 'bill_to_company', rowId: id });

  const [company, setCompany] = useState({});
  const [searchState, setSearchState] = useState({
    suggestions: [],
    showSuggestions: false,
    loading: false,
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (!mounted.current) return;
      setCompany({ country: defaultCountry });
      return;
    }
    if (id) {
      if (
        skipNextHydrationRef.current
        && skipNextHydrationRef.current.id === id
        && skipNextHydrationRef.current.syncRevision === syncKey
      ) {
        skipNextHydrationRef.current = null;
        return;
      }
      companiesService.getCompany(id).then((res) => {
        if (!mounted.current) return;
        setCompany(res);
        savedCompanyNameRef.current = res.name || '';
      }).catch(() => {
        if (!mounted.current) return;
        setCompany({});
      });
    } else {
      if (!mounted.current) return;
      setCompany(c => ({ ...c, country: defaultCountry }));
    }
  }, [id, syncKey, enabled]);

  const closeSuggestions = useCallback(() => {
    setSearchState(s => ({ ...s, showSuggestions: false }));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onNameSearch = useCallback(debounce((text) => {
    if (!mounted.current) return;
    if (!text || text.trim().length < MIN_COMPANY_SEARCH_LENGTH) {
      setSearchState(s => ({
        ...s, showSuggestions: false, suggestions: [], loading: false,
      }));
      return;
    }
    setSearchState(s => ({ ...s, loading: true }));
    companiesService.fetchCompanies({ search: text.trim(), page_size: 15 })
      .then((results) => {
        if (!mounted.current) return;
        setSearchState({
          suggestions: results,
          showSuggestions: results.length > 0,
          loading: false,
        });
      })
      .catch(() => {
        if (!mounted.current) return;
        setSearchState({ suggestions: [], showSuggestions: false, loading: false });
      });
  }, 300), []);

  const onSuggestionSelect = useCallback((suggestion) => {
    selectionMadeRef.current = true;
    setSearchState({ suggestions: [], showSuggestions: false, loading: false });

    companiesService.getCompany(suggestion.id)
      .then(companyData => companiesService.createCompany(companiesService.filterCompanyDataForPost(companyData)))
      .then((newCompany) => {
        if (!mounted.current) return;
        setCompany(newCompany);
        savedCompanyNameRef.current = newCompany.name || '';
        skipNextHydrationRef.current = { id: newCompany.id, syncRevision: syncKey };
        reportEdit('name', saved.name, newCompany.name);
        onUpdate({ [type]: newCompany.id });
        selectionMadeRef.current = false;
      })
      .catch(() => {
        if (!mounted.current) return;
        selectionMadeRef.current = false;
      });
  }, [type, onUpdate, syncKey, reportEdit, saved.name]);

  const onCompanyBlur = ({ target } = {}) => {
    // After a suggestion click, onSuggestionSelect handles the save — skip blur logic.
    if (selectionMadeRef.current) return;

    if (!target || saved[target.name] === target.value) return;
    // Non-digit/invalid ZIP format (e.g. "adsda") -- reject the edit entirely: keep showing
    // exactly what the user typed (no blanking) and skip the patch call below so an invalid
    // value never reaches the backend. `AddressForm.js` shows the "must be 5 digits or ZIP+4"
    // message for this same condition. Empty postal is allowed through (handled by the
    // separate required-field validation, not this format check).
    if (target.name === 'postal' && target.value && !isValidPostal(target.value, company.country)) {
      return;
    }
    if (target.hasMultiple) {
      target.name.forEach((name, index) => reportEdit(name, saved[name], target.value[index]));
    } else {
      reportEdit(target.name, saved[target.name], target.value);
    }
    let patch = {};
    if (target.hasMultiple) {
      target.name.forEach((name, index) => {
        patch[name] = target.value[index];
      });
    } else {
      patch = target.name !== 'country' ? { [target.name]: target.value } : {
        [target.name]: target.value,
        territory: null,
      };
    }
    if (!company.id) {
      companiesService.createCompany({ ...defaultCompanyData, ...patch })
        .then((res) => {
          if (!mounted.current) return;
          setCompany(res);
          if (target.name === 'name') savedCompanyNameRef.current = res.name || '';
          skipNextHydrationRef.current = { id: res.id, syncRevision: syncKey };
          onUpdate({ [type]: res.id });
        });
    } else if (target.name === 'name') {
      if (duplicateOnNameBlur) {
        companiesService.createCompanyHelper({ ...company, name: savedCompanyNameRef.current }, company)
          .then((res) => {
            if (!mounted.current) return;
            setCompany(res);
            savedCompanyNameRef.current = company.name || '';
            skipNextHydrationRef.current = { id: res.id, syncRevision: syncKey };
            onUpdate({ [type]: res.id });
          });
      } else {
        const patchData = companiesService.preparePatchData({ name: target.value }, company);
        companiesService.patchCompany(company.id, patchData).then((res) => {
          if (!mounted.current) return;
          setCompany(res);
          savedCompanyNameRef.current = res.name || target.value || '';
          onUpdate({ [type]: res.id });
        });
      }
    } else {
      const patchData = companiesService.preparePatchData(patch, company);
      companiesService.patchCompany(company.id, patchData).then((res) => {
        onUpdate({ [type]: res.id });
      });
    }
  };

  const onCompanyChange = ({ target }) => {
    if (eventTargetType[target.type]) {
      setCompany({ ...company, [target.name]: target.value });
      // Trigger company name search as user types
      if (target.name === 'name') {
        onNameSearch(target.value);
      }
    } else {
      let patch = {};
      if (target.hasMultiple) {
        target.name.forEach((name, index) => {
          patch[name] = target.value[index];
        });
      } else {
        patch = target.name !== 'country' ? { [target.name]: target.value } : {
          [target.name]: target.value,
          territory: null,
        };
      }
      setCompany({ ...company, ...patch });
      onCompanyBlur({ target });
    }
  };

  const onCompanyCreate = () => {
    companiesService.createCompany({
      name: null,
      country: 'United States',
      address: null,
      address2: null,
      city: null,
      territory: null,
      postal: null,
    }).then((res) => {
      if (!mounted.current) return;
      setCompany(res);
      skipNextHydrationRef.current = { id: res.id, syncRevision: syncKey };
      onUpdate({ [type]: res.id });
    });
  };

  const triggerSearch = useCallback(() => {
    const name = company.name || '';
    if (name.trim().length >= MIN_COMPANY_SEARCH_LENGTH) {
      onNameSearch(name.trim());
    }
  }, [company.name, onNameSearch]);

  return {
    company,
    onCompanyBlur,
    onCompanyCreate,
    onCompanyChange,
    searchState,
    onSuggestionSelect,
    closeSuggestions,
    triggerSearch,
  };
}

